/**
 * Shown on reports whose metrics are derived from ad spend, for clinics whose
 * spend is not synced into ad_platform.ad_spend.
 *
 * Without spend rows the cost, CAC and ROAS columns compute to zero. A row of
 * zeros reads as "we spent nothing and earned nothing", which is a different
 * and wrong claim — this says the input is missing instead.
 *
 * Reads the clinic from the auth store rather than taking props, so the pages
 * that use it need no extra state wiring. Renders nothing when the selected
 * clinic does have spend, and nothing in aggregate ("all clinics") mode, where
 * the totals are dominated by the brands that do.
 *
 * The notice also carries the connection guide, collapsed by default. It is
 * written for the brand operator, not for whoever does the wiring: the ask is
 * access plus an account id, and the AWS/BigQuery mechanics behind it are
 * deliberately absent. The guide lives here rather than on a settings page so
 * that it appears at the moment the missing data is noticed, on whichever of
 * the five spend reports the reader happens to be on.
 */

import { useState } from 'react'
import { AlertCircle, ChevronDown, ChevronRight } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { AD_SPEND_CLINICS } from '../utils/trafficClinics'

/**
 * What we need from the brand to switch a platform on.
 *
 * Meta authenticates with a Marketing API access token, not with a partner
 * access grant — src/channels/meta.py inits the SDK with the token and raises
 * without one, so an access grant alone leaves the sync unable to call the API.
 *
 * `caveats` are the things that are not obvious to the person doing this and
 * that cost real money when missed: a system user token because a personal one
 * expires and stops the sync, and shared-account campaign naming because a
 * brand whose naming nobody described lands in `uncategorized` and vanishes
 * from its own spend reporting.
 */
const PLATFORMS = [
  {
    name: 'Meta Ads',
    steps: [
      'In Meta Business Settings → Users → System Users, add a system user (or reuse an existing one) and assign it to the ad account with the "View Performance" role. Read-only is enough — we never need edit rights.',
      'Generate an access token for that system user with the ads_read permission.',
      'Send over the token together with the Ad Account ID: the numeric id in Business Settings, with or without the act_ prefix.',
    ],
    caveats: [
      'The token is a credential — please send it through a password manager or another secure channel, not over email or chat.',
      'Generate it from a system user rather than from your own account. A personal token expires after about 60 days, and when it does the nightly sync stops and this report quietly goes back to blank.',
      'If that ad account also runs campaigns for another brand, say so and tell us how this brand’s campaigns are named. Shared accounts are split by campaign name, and campaigns that match no brand are filed as uncategorized rather than counted here.',
    ],
  },
  {
    name: 'Google Ads',
    steps: [
      'In Google Ads → Admin → Access and security, grant Read only access to the Google account your 5 Axis Health contact gives you.',
      'Send over the Google Ads customer ID — ten digits, shown as XXX-XXX-XXXX.',
    ],
    caveats: [
      'Google needs no token from you: the read-only grant is what lets us set up the data transfer that carries the spend.',
      'It takes about a business day longer to switch on than Meta, because that transfer has to be created and run once before the first sync can return anything.',
    ],
  },
]

function ConnectionGuide() {
  return (
    <div className="mt-3 pt-3 border-t border-amber-200 space-y-4">
      {PLATFORMS.map(platform => (
        <div key={platform.name}>
          <h4 className="font-medium text-amber-900">{platform.name}</h4>
          <ol className="list-decimal ml-5 mt-1 space-y-1">
            {platform.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
          {platform.caveats.map((caveat, i) => (
            <p key={i} className="mt-1 text-amber-800">{caveat}</p>
          ))}
        </div>
      ))}

      <p>
        Once access is granted, spend appears on the next overnight sync, and we
        backfill roughly 90 days so the history behind this report is not blank.
      </p>

      <div>
        <h4 className="font-medium text-amber-900">Other networks</h4>
        <p className="mt-1">
          Vibe (CTV) and Everflow (affiliate) are already supported alongside Meta
          and Google, and can be connected the same way. TikTok, Snapchat, Bing and
          others are not wired up yet — contact your 5 Axis Health representative
          and we will scope what it takes.
        </p>
      </div>
    </div>
  )
}

export default function NoAdSpendNotice() {
  const { currentClinicId, clinics } = useAuthStore()
  const [showGuide, setShowGuide] = useState(false)

  if (!currentClinicId) return null
  if (AD_SPEND_CLINICS.includes(currentClinicId)) return null

  const name = clinics?.find(c => c.id === currentClinicId)?.name || 'this clinic'
  const Chevron = showGuide ? ChevronDown : ChevronRight

  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-700 text-sm">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <span>
          No ad spend is connected for {name}, so cost, CAC and ROAS are unavailable
          on this report. Traffic and conversion counts are still accurate — only the
          spend-derived figures are blank.
        </span>

        <button
          type="button"
          onClick={() => setShowGuide(v => !v)}
          aria-expanded={showGuide}
          className="flex items-center gap-1 mt-2 font-medium text-amber-800 hover:text-amber-900 hover:underline"
        >
          <Chevron className="w-4 h-4 shrink-0" />
          How to connect Meta or Google Ads
        </button>

        {showGuide && <ConnectionGuide />}
      </div>
    </div>
  )
}
